/* eslint-disable import/no-unresolved */
import { motion } from "framer-motion";
import { FC, useEffect } from "react";
import { AiFillProduct } from "react-icons/ai";
import { useIsMobile } from "~/hook/use-mobile";
import useCheckRoutIsHome from "~/hook/useCheckRouteIsHome";
import { touchVariants } from "~/lib/motion-obj";
import { useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { SheetTrigger } from "../ui/sheet";
import HeaderImage from "./HeaderImage";
import SearchBar from "./SearchBar";

type PropsType = {
  isSticky?: boolean;
  isSupperHeader?: boolean;
};
const SearchBarAndMenuMobile: FC<PropsType> = ({ isSticky, isSupperHeader = false }) => {
  const { isOpenToSearch } = useAppSelector((state) => state.search);
  const isMobile = useIsMobile();

  const { handleNavigateToHome } = useCheckRoutIsHome();

  // Prevent parent scroll when search is open
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile devices
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalHeight = document.body.style.height;

    if (isOpenToSearch) {
      // Get current scroll position
      const scrollY = window.scrollY;

      // For sticky search, we need different handling
      if (isSticky) {
        // When sticky, just prevent body scroll without changing position
        document.body.style.overflow = "hidden";
        document.body.style.position = "relative"; // Keep relative positioning for sticky to work
      } else {
        // For non-sticky, use fixed positioning
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.top = `-${scrollY}px`;
      }

      // Prevent touch scrolling and momentum scrolling on mobile
      const preventScroll = (e: TouchEvent | WheelEvent) => {
        // Allow scrolling within search results but prevent body scroll
        let target = e.target as Element;
        let isSearchScrollable = false;
        let isHorizontalScroll = false;

        // Check if it's a horizontal scroll (for tabs)
        if (e instanceof TouchEvent && e.touches.length > 0) {
          // Check if target or parent has horizontal scroll
          let checkTarget = target;
          while (checkTarget && checkTarget !== document.body) {
            const styles = window.getComputedStyle(checkTarget as HTMLElement);
            if (
              styles.overflowX === "auto" ||
              styles.overflowX === "scroll" ||
              checkTarget.classList.contains("overflow-x-auto") ||
              checkTarget.classList.contains("scrollbar-hide")
            ) {
              isHorizontalScroll = true;
              break;
            }
            checkTarget = checkTarget.parentElement as Element;
          }
        }

        // Check if the touch is within a scrollable search component
        while (target && target !== document.body) {
          if (
            target.classList.contains("search-scrollable") ||
            target.getAttribute("data-scroll") === "true" ||
            (target as HTMLElement).style.overflow === "auto" ||
            (target as HTMLElement).style.overflowY === "auto" ||
            target.classList.contains("search-results") ||
            target.closest('[data-search-results="true"]')
          ) {
            isSearchScrollable = true;
            break;
          }
          target = target.parentElement as Element;
        }

        // Allow horizontal scroll for tabs, allow vertical scroll in search areas
        if (isHorizontalScroll) {
          // Don't prevent horizontal scrolling
          return;
        }

        // If not within search scrollable area, prevent scroll
        if (!isSearchScrollable) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // Add touch event listeners
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("wheel", preventScroll, { passive: false });

      // Store cleanup function
      (window as any).__searchScrollCleanup = () => {
        document.removeEventListener("touchmove", preventScroll);
        document.removeEventListener("wheel", preventScroll);
      };
    } else {
      // Restore scrolling
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = "";
      document.body.style.top = originalTop;
      document.body.style.height = originalHeight;

      // Restore scroll position only for non-sticky
      if (!isSticky && originalTop) {
        const scrollPosition = parseInt(originalTop || "0") * -1;
        window.scrollTo(0, scrollPosition);
      }

      // Clean up event listeners
      if ((window as any).__searchScrollCleanup) {
        (window as any).__searchScrollCleanup();
        delete (window as any).__searchScrollCleanup;
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = "";
      document.body.style.top = originalTop;
      document.body.style.height = originalHeight;

      // Clean up event listeners
      if ((window as any).__searchScrollCleanup) {
        (window as any).__searchScrollCleanup();
        delete (window as any).__searchScrollCleanup;
      }
    };
  }, [isOpenToSearch, isSticky]);

  return (
    <div
      className={cn(
        " max-lg:flex hidden w-dvw xs:gap-x-1 xs:px-3 smd:px-3 sml:gap-x-1 justify-evenly md:justify-center items-center",
        // isSticky
        //   ? "fixed top-0 bg-cardBg h-16 animate-in slide-in-from-top shadow-sm duration-300 ease-in-out w-full flex justify-between smd:gap-x-3 md:gap-x-3 lg:gap-x-0 lg:hidden"
        //   : "max-lg:flex hidden w-dvw xs:gap-x-1 xs:px-3 smd:px-3 sml:gap-x-1 justify-evenly md:justify-center items-center z-20",
      )}
    >
      {!isOpenToSearch && isSticky && (
        <div className="hidden lg:flex items-center justify-between gap-x-1">
          <HeaderImage />
        </div>
      )}

      <SearchBar isSticky={isSticky} isSupperHeader={isSupperHeader} />

      {/* {!isOpenToSearch && <ChangeLanguage variant="footer" />} */}

      {!isOpenToSearch && (
        <div>
          <SheetTrigger>
            <motion.div
              title="menu"
              variants={touchVariants}
              initial="initial"
              whileTap="tap"
              className="flex size-[2.3rem] rounded-full justify-center items-center bg-textHeadingColor"
            >
              <AiFillProduct size={18} color="#fff " />
            </motion.div>
          </SheetTrigger>
        </div>
      )}
    </div>
  );
};

export default SearchBarAndMenuMobile;
